// Purchase tracking (#736): the browser half of the Meta `Purchase` event, and
// the InitiateCheckout pair at the same Pay click (#1368).
//
// The Purchase itself is sent server-side when the deposit is recorded
// (trg_notify_meta_purchase -> meta-capi), when no page of ours is open. For it
// to count as a `website` event Meta needs the customer's browser context, so
// this records it at the Pay click on the public quote page. meta-capi stores it
// only for an accepted, unpaid quote that came from a Meta ad (has an fbclid).
//
// The same call carries a fresh `event_id`. When meta-capi stores the context
// and sends its InitiateCheckout with that id, it answers
// `initiate_checkout_sent: true`, and only then does the browser fire the image
// pixel with the same id. So both legs pass the Purchase's gates, and neither
// fires alone.
//
// Gated on `analyticsAllowed()` and a production host, like trackLeadConversion
// (lead-tracking.ts): a customer who declined tracking stores no context, and a
// quote with no context never sends a Purchase.

import { supabase } from "@/integrations/supabase/client";
import { getFbp, getFbc, sanitizeTrackedPath, sendPixelBeacon } from "@/lib/analytics";
import { analyticsAllowed } from "@/lib/consent";
import { trackingHostAllowed } from "@/lib/tracking-host";

/**
 * Record the browser context for a quote's Purchase, and fire the
 * InitiateCheckout pair. The quote id travels only as the lookup key, never
 * inside the page URL (the server re-sanitizes it too). Sends no email, phone or
 * amounts to meta-capi; `total` is only the browser pixel's value.
 *
 * Fire-and-forget and fully swallowed: tracking must NEVER fail a payment.
 */
export function recordCheckoutContext(quoteId: string, total?: number | string | null): void {
  try {
    if (!analyticsAllowed() || !trackingHostAllowed()) return;
    let eventId: string | undefined;
    try {
      eventId = crypto.randomUUID();
    } catch {
      // No randomUUID (old browser): the context still stores, with no InitiateCheckout.
    }
    supabase.functions
      .invoke("meta-capi", {
        body: {
          mode: "checkout_context",
          quote_id: quoteId,
          ua: navigator.userAgent,
          fbp: getFbp(),
          fbc: getFbc(),
          source_url: window.location.origin + sanitizeTrackedPath(window.location.pathname),
          ...(eventId ? { event_id: eventId } : {}),
        },
      })
      .then(({ data, error }) => {
        if (error) {
          console.warn("[meta-capi] checkout context failed", error);
          return;
        }
        if (eventId && data?.initiate_checkout_sent === true) sendPixelBeacon("InitiateCheckout", eventId, Number(total));
      })
      .catch((err) => console.warn("[meta-capi] checkout context threw", err));
  } catch (err) {
    console.warn("[analytics] checkout context threw", err);
  }
}
